import { qualityApi } from "../../lib/api";
import type { JsonRecord, WorkContext } from "../../types";
import type {
  ActivityMaterialDeliverable,
  ActivityMaterialDeliverableCatalog,
  ActivityMaterialDeliverablesDetail,
  ActivityMaterialCatalogItem,
  AvailableSpecification,
  ComplianceLimit,
  ContractArea,
  ContractActivityMaterial,
  DeliverableCatalogItem,
  SamplingFrequency,
  Specification,
  SpecificationDeliverable,
  SpecificationDetail,
  SpecificationTest,
  TestCatalogItem,
  UnitOfMeasure,
} from "./types";

const record = (value: unknown): JsonRecord =>
  value && typeof value === "object" && !Array.isArray(value) ? value as JsonRecord : {};

export function unwrapData(value: unknown): unknown {
  const root = record(value);
  return root.data ?? root.resultado ?? root.result ?? value;
}

export function rows(value: unknown, keys: string[] = []): JsonRecord[] {
  const unwrapped = unwrapData(value);
  if (Array.isArray(unwrapped)) return unwrapped.filter(isRecord);
  const root = record(unwrapped);
  for (const key of [...keys, "items", "rows", "registros"]) {
    if (Array.isArray(root[key])) return (root[key] as unknown[]).filter(isRecord);
  }
  return [];
}

const isRecord = (value: unknown): value is JsonRecord =>
  Boolean(value && typeof value === "object" && !Array.isArray(value));
const str = (row: JsonRecord, ...keys: string[]) => {
  for (const key of keys) if (row[key] !== null && row[key] !== undefined) return String(row[key]);
  return "";
};
const bool = (row: JsonRecord, key: string, fallback = true) =>
  typeof row[key] === "boolean" ? row[key] as boolean : fallback;
const inherited = (row: JsonRecord) => {
  const contractualOrigins = ["contractual", "particular", "especifica", "específica"];
  return bool(row, "heredado", bool(
    row,
    "heredada",
    !contractualOrigins.includes(str(row, "origen").toLowerCase()),
  ));
};
const origin = (row: JsonRecord) => inherited(row) ? "generico" as const : "contractual" as const;

function material(row: JsonRecord): ContractActivityMaterial {
  const id = str(row, "contrato_actividad_material_id", "id");
  const categoria = str(row, "categoria", "tipo", "tipo_nombre");
  return {
    ...row,
    id,
    contrato_actividad_material_id: id,
    actividad_material_id: str(row, "actividad_material_id") || undefined,
    nombre: str(row, "nombre_contractual", "nombre", "actividad_material"),
    nombre_contractual: str(row, "nombre_contractual") || undefined,
    clave: str(row, "clave_contractual", "clave") || undefined,
    clase: str(row, "clase", "tipo_elemento").toLowerCase() === "actividad" ? "actividad" : "material",
    categoria: categoria || undefined,
    tipo: categoria || undefined,
    notas: str(row, "notas") || undefined,
    origen: origin(row),
    is_active: bool(row, "is_active", bool(row, "activo")),
    puede_editar: typeof row.puede_editar === "boolean" ? row.puede_editar : undefined,
  };
}

function specification(row: JsonRecord): Specification {
  const contractualId = str(row, "especificacion_id", "especificacion_proyecto_id", "id");
  return {
    ...row,
    id: contractualId,
    especificacion_proyecto_id: contractualId,
    especificacion_id: contractualId,
    especificaciones_default_id: str(row, "especificaciones_default_id") || null,
    contrato_actividad_material_id: str(row, "contrato_actividad_material_id") || undefined,
    codigo: str(row, "codigo", "clave"),
    titulo: str(row, "titulo", "nombre"),
    organizacion: str(row, "organizacion") || undefined,
    version: str(row, "version") || undefined,
    fecha_publicacion: str(row, "fecha_publicacion") || undefined,
    fecha_vigencia: str(row, "fecha_vigencia") || undefined,
    referencia_seccion: str(row, "referencia_seccion") || undefined,
    documento_url: str(row, "documento_url") || undefined,
    notas: str(row, "notas") || undefined,
    origen: origin(row),
    is_active: bool(row, "is_active", bool(row, "activo")),
    puede_editar: typeof row.puede_editar === "boolean" ? row.puede_editar : undefined,
  };
}

function limit(row: JsonRecord): ComplianceLimit {
  return {
    ...row,
    id: str(row, "limite_id", "especificacion_entregable_prueba_limite_id", "id"),
    tipo_criterio: (str(row, "tipo_criterio") || "texto") as ComplianceLimit["tipo_criterio"],
    valor_minimo: typeof row.minimo === "number" ? row.minimo : null,
    valor_maximo: typeof row.maximo === "number" ? row.maximo : null,
    valor_objetivo: typeof row.valor_numerico === "number" ? row.valor_numerico : null,
    tolerancia_inferior: typeof row.tol_min === "number" ? row.tol_min : null,
    tolerancia_superior: typeof row.tol_max === "number" ? row.tol_max : null,
    criterio_textual: str(row, "valor_alfa") || undefined,
    unidad: str(row, "unidad") || undefined,
    heredado: inherited(row),
    is_active: bool(row, "is_active", bool(row, "activo")),
  };
}

function test(row: JsonRecord): SpecificationTest {
  const id = str(row, "especificacion_entregable_prueba_id", "id");
  return {
    ...row,
    id,
    especificacion_entregable_prueba_id: id,
    prueba_id: str(row, "prueba_id") || undefined,
    prueba: str(row, "prueba", "nombre"),
    clave: str(row, "clave") || undefined,
    obligatoria: bool(row, "obligatoria", false),
    aplica: bool(row, "aplica"),
    is_active: bool(row, "is_active", bool(row, "activo")),
    heredada: inherited(row),
    limites: rows(row.limites, ["limites_cumplimiento"]).map(limit),
  };
}

function frequency(row: JsonRecord): SamplingFrequency {
  return {
    ...row,
    id: str(row, "frecuencia_id", "especificacion_entregable_frecuencia_id", "id"),
    unidad_lote: str(row, "unidad") || undefined,
    heredado: inherited(row),
    is_active: bool(row, "is_active", bool(row, "activo")),
  };
}

function deliverable(row: JsonRecord): SpecificationDeliverable {
  const id = str(row, "especificacion_entregable_id", "id");
  return {
    ...row,
    id,
    especificacion_entregable_id: id,
    entregable_id: str(row, "entregable_id") || undefined,
    entregable: str(row, "entregable", "nombre"),
    aplica: bool(row, "aplica"),
    is_active: bool(row, "is_active", bool(row, "activo")),
    heredado: inherited(row),
    frecuencias: rows(row.frecuencias, ["frecuencias_muestreo"]).map(frequency),
    pruebas: rows(row.pruebas, ["tests"]).map(test),
  };
}

const callMaterials = (operation: string, context: WorkContext, payload: JsonRecord = {}) =>
  qualityApi("api_calidad_actividades_materiales", operation, context, payload);
const callSpecs = (operation: string, context: WorkContext, payload: JsonRecord = {}) =>
  qualityApi("api_calidad_especificaciones", operation, context, payload);
const callDeliverables = (operation: string, context: WorkContext, payload: JsonRecord = {}) =>
  qualityApi("api_calidad_entregables", operation, context, payload);

const area = (row: JsonRecord): ContractArea => ({ id: str(row, "area_id", "id"), area: str(row, "area", "nombre") });
const activityMaterialDeliverable = (row: JsonRecord): ActivityMaterialDeliverable => ({
  entregable_id: str(row, "entregable_id", "id"), entregable: str(row, "entregable", "nombre"),
  directo: bool(row, "directo", false), heredado: bool(row, "heredado", false),
  area_id: str(row, "area_id") || null, area: str(row, "area") || undefined,
  especificaciones: rows(row.especificaciones).map((spec) => ({
    especificacion_id: str(spec, "especificacion_id", "id"), codigo: str(spec, "codigo") || undefined,
    titulo: str(spec, "titulo") || undefined,
  })),
});

export async function getActivityMaterialDeliverables(context: WorkContext, contractActivityMaterialId: string): Promise<ActivityMaterialDeliverablesDetail> {
  const root = record(unwrapData(await callDeliverables("am_detalle", context, { contrato_actividad_material_id: contractActivityMaterialId })));
  return { entregables: rows(root.entregables).map(activityMaterialDeliverable), areas: rows(root.areas).map(area) };
}
export async function getActivityMaterialDeliverableCatalog(context: WorkContext): Promise<ActivityMaterialDeliverableCatalog> {
  const root = record(unwrapData(await callDeliverables("catalogo", context)));
  return {
    entregables: rows(root.entregables).map((row) => ({ id: str(row, "entregable_id", "id"), nombre: str(row, "entregable", "nombre"), area_id: str(row, "area_id") || null, area: str(row, "area") || undefined })),
    areas: rows(root.areas).map(area),
  };
}
export const assignDirectActivityMaterialDeliverable = (context: WorkContext, contractActivityMaterialId: string, deliverableId: string, areaId: string) =>
  callDeliverables("am_asignar_directo", context, { contrato_actividad_material_id: contractActivityMaterialId, entregable_id: deliverableId, area_id: areaId });
export const setDirectActivityMaterialDeliverableActive = (context: WorkContext, contractActivityMaterialId: string, deliverableId: string) =>
  callDeliverables("am_desactivar_directo", context, { contrato_actividad_material_id: contractActivityMaterialId, entregable_id: deliverableId });
export const setActivityMaterialDeliverableArea = (context: WorkContext, deliverableId: string, areaId: string) =>
  callDeliverables("area_asignar", context, { entregable_id: deliverableId, area_id: areaId });

export const getAssignedMaterials = async (context: WorkContext) =>
  rows(await callMaterials("am_asignados", context), ["materiales", "asignados"]).map(material);
export const getMaterialCatalog = async (context: WorkContext) =>
  rows(await callMaterials("am_catalogo", context), ["catalogo"]).map((row): ActivityMaterialCatalogItem => ({
    id: str(row, "actividad_material_id", "id"),
    nombre: str(row, "nombre"),
    clave: str(row, "clave") || undefined,
    clase: str(row, "clase").toLowerCase() === "actividad" ? "actividad" : "material",
    tipo: str(row, "tipo") || undefined,
  }));
export const assignMaterial = (context: WorkContext, id: string) =>
  callMaterials("am_asignar_generico", context, { actividad_material_id: id });
export const createMaterial = (context: WorkContext, data: JsonRecord) =>
  callMaterials("am_crear_contractual", context, data);
export const updateMaterial = (context: WorkContext, id: string, data: JsonRecord) =>
  callMaterials("am_actualizar_contractual", context, { contrato_actividad_material_id: id, ...data });
export const setMaterialActive = (context: WorkContext, id: string, active: boolean) =>
  callMaterials(active ? "am_reactivar_asignacion" : "am_desactivar_asignacion", context, {
    contrato_actividad_material_id: id,
  });

export async function getSpecifications(context: WorkContext, materialId: string) {
  const result = await callSpecs("spec_proyecto", context, { contrato_actividad_material_id: materialId });
  const all = rows(result, ["especificaciones"]).map(specification);
  return all.filter((item) => {
    const raw = item as unknown as JsonRecord;
    const relatedId = str(raw, "contrato_actividad_material_id");
    return !relatedId || relatedId === materialId;
  });
}
export const getAvailableSpecifications = async (context: WorkContext, materialId: string) =>
  rows(await callMaterials("am_especificaciones_disponibles", context, {
    contrato_actividad_material_id: materialId,
  }), ["especificaciones", "disponibles"]).map((raw): AvailableSpecification => {
    const defaultSpecificationId = str(raw, "especificacion_default_id", "id");
    if (!defaultSpecificationId) {
      throw new Error("La especificación seleccionada no contiene especificacion_default_id.");
    }
    return {
      id: defaultSpecificationId,
      especificacion_default_id: defaultSpecificationId,
      codigo: str(raw, "codigo"),
      titulo: str(raw, "titulo"),
      organizacion: str(raw, "organizacion"),
      version: raw.version == null ? null : String(raw.version),
      orden: typeof raw.orden === "number" ? raw.orden : undefined,
      asignada: bool(raw, "asignada", false),
    };
  });

export function assignDefaultSpecification(
  context: WorkContext,
  contractActivityMaterialId: string,
  specificationDefaultId: string,
) {
  if (!contractActivityMaterialId) {
    throw new Error("No se pudo identificar el material contractual seleccionado.");
  }
  if (!specificationDefaultId) {
    throw new Error("No se pudo identificar la especificación genérica seleccionada.");
  }
  return callSpecs("spec_asignar_default", context, {
    contrato_actividad_material_id: contractActivityMaterialId,
    especificacion_default_id: specificationDefaultId,
  });
}
export const createSpecification = (
  context: WorkContext,
  materialId: string,
  data: JsonRecord,
  deliverableIds: string[],
) => callSpecs("spec_crear_contractual", context, {
  contrato_actividad_material_id: materialId,
  ...data,
  entregables: deliverableIds.map((entregable_id) => ({ entregable_id })),
});
export const updateSpecification = (context: WorkContext, id: string, data: JsonRecord) =>
  callSpecs("spec_actualizar_contractual", context, { especificacion_id: id, ...data });
export const setSpecificationActive = (context: WorkContext, id: string, active: boolean) =>
  callSpecs(active ? "spec_reactivar" : "spec_desactivar", context, { especificacion_id: id });

export async function getSpecificationDetail(
  context: WorkContext,
  specificationProjectId: string,
): Promise<SpecificationDetail> {
  if (!specificationProjectId) {
    throw new Error("No se pudo identificar la especificación contractual.");
  }
  const raw = unwrapData(await callSpecs("spec_detalle", context, {
    especificacion_id: specificationProjectId,
  }));
  const root = record(raw);
  const specificationRow = record(root.especificacion ?? root.specification);
  return {
    especificacion: specification({ ...specificationRow, especificacion_id: specificationProjectId }),
    entregables: rows(root.entregables, ["deliverables"]).map(deliverable),
  };
}

export const detailMutation = (context: WorkContext, operation: string, payload: JsonRecord) =>
  callSpecs(operation, context, payload);

export async function getDetailCatalogs(context: WorkContext): Promise<{
  unidades: UnitOfMeasure[];
  pruebas: TestCatalogItem[];
  entregables: DeliverableCatalogItem[];
}> {
  const [unitsResult, testsResult, deliverablesResult] = await Promise.all([
    callSpecs("spec_unidades_catalogo", context), callSpecs("spec_pruebas_catalogo", context), callDeliverables("catalogo", context),
  ]);
  const deliverablesRoot = record(unwrapData(deliverablesResult));
  return {
    unidades: rows(unitsResult).map((row) => ({ id: str(row, "id"), nombre: str(row, "nombre"), simbolo: str(row, "simbolo") || undefined })),
    pruebas: rows(testsResult).map((row) => ({ id: str(row, "id"), nombre: str(row, "prueba", "nombre"), clave: str(row, "clave") || undefined })),
    entregables: rows(deliverablesRoot.entregables).map((row) => ({ id: str(row, "entregable_id", "id"), nombre: str(row, "entregable", "nombre"), clave: str(row, "clave") || undefined })),
  };
}

export function mutationId(value: unknown): string | undefined {
  const root = record(unwrapData(value));
  return str(root, "especificacion_id", "contrato_actividad_material_id", "especificacion_proyecto_id", "id") || undefined;
}
